import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { send, notify } from "../../../services/NotificationService";
import {
    Form,
    useLoaderData
} from "react-router-dom";

import { getUserProfile, getRentalsHistory, getTools, linkUser, deleteAccount, logoutUser, addUserModule, removeUserModule } from "../../../data/admin/userProfiles";
import { getSettings } from "../../../data/loca/settings";

import React, { useMemo, useState } from 'react';

const AVAILABLE_MODULES = [
    { label: "Money", value: "MoneyModule" },
    { label: "Loca", value: "LocaModule" },
    { label: "Garden", value: "GardenModule" },
    { label: "Tipz", value: "TipzModule" }
];

export async function loader({ params }) {
    const user = await getUserProfile(params.userId);
    const rentals = await getRentalsHistory(params.userId);
    const tools = await getTools(params.userId);
    const settings = await getSettings();
    return { user, rentals, tools, settings };
}

export default function User() {
    const { user, rentals = [], tools = [], settings } = useLoaderData();
    const [selectedModule, setSelectedModule] = useState(AVAILABLE_MODULES[0].value);
    const [isAddingModule, setIsAddingModule] = useState(false);

    const activeModules = useMemo(() => user?.activeModules ?? user?.ActiveModules ?? [], [user]);
    const availableModules = useMemo(() => AVAILABLE_MODULES.filter((m) => !activeModules.includes(m.value)), [activeModules]);
    const lastLoginLabel = user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("fr-FR") : "Jamais";
    const isValidatedLabel = user?.isValidated ? "Oui" : "Non";

    if (!user) {
        return <div id="mainContent"><h2>Utilisateur indisponible</h2><p>La fiche utilisateur n'a pas pu être chargée.</p></div>;
    }

    const switchAccount = () => {
        linkUser(user.publicId).then(() => {
            window.location.href = "/login/switch/" + user.publicId;
        });
    };

    const accept = () => {
        deleteAccount(user.publicId).then(() => {
            send("success", "Le compte a été supprimé !");
            notify();
            window.location.href = "/user/list";
        });
    };

    const reject = () => { };

    const deleteAccountAction = (event) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Êtes-vous sûr de vouloir supprimer ce compte ?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Oui',
            rejectLabel: 'Non',
            accept: () => { accept(); },
            reject
        });
    };

    const confirmLogout = (event) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Êtes-vous sûr de vouloir déconnecter cet utilisateur ?",
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-warning',
            acceptLabel: 'Oui',
            rejectLabel: 'Non',
            accept: () => {
                logoutUser(user.publicId).then((response) => {
                    if (response.status === 200)
                        send("success", response.message);
                    else
                        send("error", response.message);

                    notify();
                });
            },
            reject
        });
    };

    const handleAddModule = () => {
        const moduleToAdd = availableModules.some((m) => m.value === selectedModule)
            ? selectedModule
            : (availableModules[0]?.value ?? "");

        if (!moduleToAdd) {
            send("warn", "Aucun module disponible à ajouter.");
            notify();
            return;
        }

        setIsAddingModule(true);
        addUserModule(user.userId, moduleToAdd)
            .then((response) => {
                if (response?.status === 200) {
                    send("success", response.message || "Module ajouté.");
                    notify();
                    window.location.reload();
                    return;
                }

                send("error", response?.message || "Impossible d'ajouter le module.");
                notify();
            })
            .catch(() => {
                send("error", "Impossible d'ajouter le module.");
                notify();
            })
            .finally(() => setIsAddingModule(false));
    };


    const handleRemoveModule = (moduleType) => {
        if (!moduleType) {
            return;
        }

        removeUserModule(user.userId, moduleType)
            .then((response) => {
                if (response?.status === 200) {
                    send("success", response.message || "Module supprimé.");
                    notify();
                    window.location.reload();
                    return;
                }

                send("error", response?.message || "Impossible de supprimer le module.");
                notify();
            })
            .catch(() => {
                send("error", "Impossible de supprimer le module.");
                notify();
            });
    };

    const listingLinkForTool = (tool) => {
        const baseUrl = (settings?.publicSiteUrl ?? settings?.PublicSiteUrl ?? "https://lokapp.fr").replace(/\/+$/, '');
        const customUrl = tool?.customUrl ?? tool?.CustomUrl;
        const publicId = tool?.publicId ?? tool?.PublicId;
        const legacySlug = customUrl || publicId;
        if (!legacySlug) {
            return `${baseUrl}/catalogue`;
        }
        if (legacySlug.startsWith('http://') || legacySlug.startsWith('https://')) {
            return legacySlug;
        }
        if (legacySlug.startsWith('/')) {
            return `${baseUrl}${legacySlug}`;
        }
        if (legacySlug.includes('/outil/')) {
            return `${baseUrl}/${legacySlug}`;
        }
        return `${baseUrl}/outil/${legacySlug}`;
    };

    return (
        <div>
            <div id="thumbnail">
                <div className="clientPicture">
                    <i className="pi pi-user" style={{ fontSize: '10rem', color: '#4c566a' }}></i>
                </div>

                <div>
                    <h1>
                        {user.firstname}  {user.lastname}
                    </h1>

                    <div>
                        <Form action="modification">
                            <Button type="submit" size="small" className="user-action-edit">Modifier l'utilisateur</Button>
                        </Form>
                        <Button onClick={switchAccount} icon="pi pi-arrow-right-arrow-left" size="small" type="submit" severity="secondary" data-cy="switchBtn" className="user-action-switch"></Button>
                        <Button onClick={confirmLogout} icon="pi pi-sign-out" size="small" type="button" severity="warning" data-cy="logoutUser" className="user-action-logout"></Button>
                        <Button onClick={deleteAccountAction} icon="pi pi-trash" label="" size="small" className="p-button-danger user-action-delete" data-cy="deleteAccount"></Button>

                        <ConfirmPopup />
                        <Tooltip target=".user-action-edit" content="Modifier les informations utilisateur" />
                        <Tooltip target=".user-action-switch" content="Se connecter en tant qu'utilisateur" />
                        <Tooltip target=".user-action-logout" content="Forcer la déconnexion" />
                        <Tooltip target=".user-action-delete" content="Supprimer le compte utilisateur" />
                    </div>
                    <label>
                        <div><b>Email :</b> {user.email}</div>
                        <div><b>Téléphone :</b> {user.phone}</div>
                        <div><b>Adresse :</b> {user.address}</div>
                        <div><b>Compte validé :</b> {isValidatedLabel}</div>
                        <div><b>Dernière connexion :</b> {lastLoginLabel}</div>
                    </label>

                </div>

            </div>
            <div id="mainContent">
                <h2>Modules d'accès</h2>
                <div style={{ marginBottom: 18 }}>
                    <p><b>Modules actifs (Money/Loca/Garden) :</b></p>
                    {activeModules.length === 0 && <p>Aucun module actif</p>}
                    {activeModules.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                            {activeModules.map((moduleName) => (
                                <span key={moduleName} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', border: '1px solid #ddd', borderRadius: 16, padding: '4px 8px' }}>
                                    {moduleName}
                                    <Button
                                        icon="pi pi-times"
                                        severity="danger"
                                        size="small"
                                        text
                                        rounded
                                        onClick={() => handleRemoveModule(moduleName)}
                                        aria-label={`Supprimer ${moduleName}`}
                                    />
                                </span>
                            ))}
                        </div>
                    )}
                    {availableModules.length === 0 ? (
                        <p>Tous les modules sont déjà associés.</p>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                value={availableModules.some((m) => m.value === selectedModule) ? selectedModule : availableModules[0].value}
                                onChange={(e) => setSelectedModule(e.target.value)}
                                style={{ minWidth: 180 }}
                            >
                                {availableModules.map((module) => (
                                    <option key={module.value} value={module.value}>{module.label}</option>
                                ))}
                            </select>
                            <Button
                                label="Ajouter le module"
                                size="small"
                                icon="pi pi-plus"
                                onClick={handleAddModule}
                                loading={isAddingModule}
                            />
                        </div>
                    )}
                </div>

                <h2>Historique</h2>
                {rentals.length === 0 && (
                    <i>Aucune location pour cet outil</i>
                )}
                {rentals.length > 0 && (

                    <div>
                        <table className="historyTable">
                            <thead>
                                <tr>
                                    <th>Outil</th>
                                    <th>Début</th>
                                    <th>Fin</th>
                                    <th>Prix</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rentals.map((rental) => (

                                    <tr key={rental.id}>
                                        <td><a href={"/" + rental.rentalPublicId}>{rental.toolName}</a></td>
                                        <td>{new Date(rental.startDate).toLocaleDateString()}</td>
                                        <td>{new Date(rental.endDate).toLocaleDateString()}</td>
                                        <td>{rental.totalAmount / 100}&euro;</td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                        <div>
                            <p><b>Total :</b> {rentals.length}</p>
                            <p><b>Pour un montant de :</b> {rentals.reduce((acc, rental) => acc + rental.totalAmount, 0) / 100}&euro;</p>

                        </div>
                    </div>
                )}

                <h2>Annonces</h2>
                {tools.length === 0 && (
                    <i>Aucun outil</i>
                )}
                {tools.length > 0 && (

                    <div>
                        <table className="historyTable">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Lien</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tools.map((tool) => (
                                    <tr key={tool.id}>
                                        <td>{tool.name}</td>
                                        <td><a href={listingLinkForTool(tool)} rel="noopener noreferrer" target="_blank">{listingLinkForTool(tool)}</a></td>
                                        <td>
                                            <Button
                                                label="Modifier l'annonce"
                                                size="small"
                                                severity="secondary"
                                                onClick={() => {
                                                    if (tool.publicId) {
                                                        window.location.href = `/monoutil/${tool.publicId}/modification`;
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>

                    </div>
                )}
            </div>
        </div>
    );
}
